import {getNameByISO3} from '../../../../libs/misc'
import {
  AffectedFlowRow,
  FLOW_IDX,
  GroupDetail,
  PreferentialTradePartnersResponse,
  SimulationMeta,
  SimulationResponse,
  SimulationSummary,
} from './_models'

export function filterAvailableGroups(resp: PreferentialTradePartnersResponse): string[] {
  return (resp.group_partners || []).filter((code) => {
    const g = resp.group_details?.[code]
    return !!g && Array.isArray(g.group_reporters) && g.group_reporters.length > 0
  })
}

export function hasNoPartners(resp: PreferentialTradePartnersResponse): boolean {
  const bilateral = resp.bilateral_partners || []
  return bilateral.length === 0 && filterAvailableGroups(resp).length === 0
}

export function calcImportPct(
  summary: SimulationSummary,
  currentTrade: Record<string, number>
): string {
  const ratio =
    Math.abs(summary.result_sum_trade_effect_total) / (currentTrade?.total_sum as number)
  if (isNaN(ratio)) return 'An unknown percentage'
  if (ratio < 0.0001) return '<0.01%'
  return Math.round(ratio * 10000) / 100 + '%'
}

export function calcEconPct(totalEffect: number, econImport: number | undefined): string {
  if (econImport === undefined) return 'N/A'
  if (econImport === 0 && totalEffect < 0) return '<-0.01%'
  return Math.round((totalEffect / econImport) * 10000) / 100 + '%'
}

export function decodeTariffCode(
  code: string,
  econ: string,
  groups: Record<string, GroupDetail>
): string {
  if (!code) return 'N/A'
  if (code.startsWith('pref:')) {
    const [fromIso3, partyCode] = code.replace('pref:', '').split('-')
    if (partyCode === econ) {
      return `${getNameByISO3(fromIso3)}-${getNameByISO3(econ)} (bilateral)`
    }
    return groups?.[partyCode]?.title ?? 'N/A'
  }
  if (code.startsWith('mfn')) return 'MFN rate'
  if (code.startsWith('bound')) return 'Bound rate'
  return 'N/A'
}

export function lossTooltip(
  loss: number,
  tariffEqual: number,
  econName: string,
  hs: string
): string {
  if (loss === 0 && tariffEqual === 1) return 'tariff has not changed'
  if (loss === 0 && tariffEqual === 0) return `inelastic demand of ${econName} for commodity ${hs}`
  return ''
}

export function buildAffectedFlowRows(resp: SimulationResponse): AffectedFlowRow[] {
  return (resp.table_data || []).map((r) => ({
    econ: r[FLOW_IDX.econ],
    hs: r[FLOW_IDX.hs],
    hsDescription: resp.hs_descriptions?.[r[FLOW_IDX.hs]] ?? 'N/A',
    oldTariff: r[FLOW_IDX.oldTariff],
    oldTariffCode: r[FLOW_IDX.oldTariffCode],
    newTariff: r[FLOW_IDX.newTariff],
    newTariffCode: r[FLOW_IDX.newTariffCode],
    currentImportValue: r[FLOW_IDX.currentImportValue],
    tradeLoss: r[FLOW_IDX.tradeLoss],
    elasticity: r[FLOW_IDX.elasticity],
    tariffEqual: r[FLOW_IDX.tariffEqual],
    tradeDiversion: r[FLOW_IDX.tradeDiversion],
    totalLossGain: r[FLOW_IDX.totalLossGain],
    pctChange: r[FLOW_IDX.pctChange],
  }))
}

export function noDataMessage(meta: SimulationMeta): string {
  const potential = meta.counter_potentially_calculate || 0
  if (potential > 0 && meta.counter_data_no_elas_demand >= potential) {
    return 'No import demand elasticity data is available for the affected commodity flows, so the impact cannot be estimated.'
  }
  if ((meta.counter_data_no_lower_tariff || 0) > 0) {
    return 'Tariffs changed for the selected agreements, but none of the new tariffs are lower than the current applied rate, so no commodity flows are recorded.'
  }
  return 'No commodity flows could be recorded for the selected agreements and parameters.'
}
